const DATABASE_NAME = 'revival-site-audit-drafts';
const STORE_NAME = 'drafts';
export const checklistDraftKey = (userId:string,projectId:string) => `checklist:${userId}:${projectId}`;
const encodedMedia = new WeakMap<Blob,Promise<ArrayBuffer>>();
export type StoredDraft<T> = { projectId: string; savedAt: string; payload: T };
const temporary = new Map<string, StoredDraft<unknown> | null>();
const writes = new Map<string, Promise<unknown>>();
export const draftStorageMode = (key: string) => temporary.has(key) ? 'tab' : 'device';

// ArrayBuffers avoid browsers that reject File/Blob records in private windows.
// Encode before opening the transaction, so it cannot expire during conversion.
export async function encodeDraft(value: unknown, portable = false): Promise<unknown> {
  if (value instanceof Blob) {
    let buffer = encodedMedia.get(value); if(!buffer) {buffer=value.arrayBuffer();encodedMedia.set(value,buffer);}
    const bytes = await buffer;
    let data: ArrayBuffer | string = bytes;
    if (portable) { let binary = ''; const array = new Uint8Array(bytes); for (let i = 0; i < array.length; i += 8192) binary += String.fromCharCode(...array.subarray(i, i + 8192)); data = btoa(binary); }
    return { __snagMedia: 1, type: value.type, name: value instanceof File ? value.name : 'evidence', modified: value instanceof File ? value.lastModified : 0, data };
  }
  if (Array.isArray(value)) return Promise.all(value.map((item) => encodeDraft(item, portable)));
  if (value && typeof value === 'object') return Object.fromEntries(await Promise.all(Object.entries(value).map(async ([key, item]) => [key, await encodeDraft(item, portable)])));
  return value;
}
export function decodeDraft(value: any): any {
  if (value instanceof Blob) return value;
  if (value?.__snagMedia === 1) {
    const bytes = typeof value.data === 'string' ? Uint8Array.from(atob(value.data), (c) => c.charCodeAt(0)) : value.data;
    return new File([bytes], value.name, { type: value.type, lastModified: value.modified });
  }
  if (Array.isArray(value)) return value.map(decodeDraft);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key,item]) => [key, decodeDraft(item)]));
  return value;
}
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => { settled = true; reject(new Error('Device storage did not respond')); }, 5000);
    try {
      const request = indexedDB.open(DATABASE_NAME, 1);
      request.onupgradeneeded = () => { if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: 'projectId' }); };
      request.onsuccess = () => { clearTimeout(timer); if (settled) request.result.close(); else { settled = true; resolve(request.result); } };
      request.onerror = request.onblocked = () => { clearTimeout(timer); settled = true; reject(request.error ?? new Error('Device storage is unavailable')); };
    } catch (error) { clearTimeout(timer); settled = true; reject(error); }
  });
}
async function readRecords(key?: string): Promise<Array<StoredDraft<unknown>>> {
  const db = await openDatabase();
  try { return await new Promise((resolve,reject) => {
    const tx=db.transaction(STORE_NAME,'readonly'); const store=tx.objectStore(STORE_NAME); const req=key === undefined ? store.getAll() : store.get(key);
    const timer=setTimeout(()=>{try{tx.abort();}catch{}reject(new Error('Device storage did not respond'));},5000);
    req.onsuccess=()=>{clearTimeout(timer);try{resolve((key===undefined ? req.result : req.result ? [req.result] : []).map(decodeDraft));}catch(error){reject(error);}};
    req.onerror=()=>{clearTimeout(timer);reject(req.error);};tx.onabort=()=>{clearTimeout(timer);reject(tx.error ?? new Error('Device read was interrupted'));};
  }); }
  finally { db.close(); }
}
export async function listAuditDrafts<T>(): Promise<Array<StoredDraft<T>>> {
  const records = new Map((await readRecords().catch(() => [])).map((item) => [item.projectId,item]));
  for (const [key,value] of temporary) { if (value) records.set(key,value); else records.delete(key); }
  return [...records.values()].sort((a,b) => b.savedAt.localeCompare(a.savedAt)) as Array<StoredDraft<T>>;
}
export async function loadAuditDraft<T>(key: string): Promise<StoredDraft<T> | null> {
  await writes.get(key)?.catch(() => undefined);
  if (temporary.has(key)) return (temporary.get(key) ?? null) as StoredDraft<T> | null;
  return ((await readRecords(key).catch(()=>[]))[0] as StoredDraft<T> | undefined) ?? null;
}
function serialize<T>(key: string, action: () => Promise<T>): Promise<T> {
  const next = (writes.get(key) ?? Promise.resolve()).catch(() => undefined).then(action);
  writes.set(key,next); void next.finally(() => { if (writes.get(key) === next) writes.delete(key); }).catch(() => undefined); return next;
}
async function writeRecord(key: string, value: StoredDraft<unknown> | null) {
  const encoded = value ? await encodeDraft(value) : null;
  const db = await openDatabase();
  try { await new Promise<void>((resolve,reject) => {
    const tx = db.transaction(STORE_NAME,'readwrite');
    const timer = setTimeout(() => { try { tx.abort(); } catch {} reject(new Error('Device storage did not finish saving')); }, 5000);
    tx.oncomplete = () => { clearTimeout(timer); resolve(); };
    tx.onerror = tx.onabort = () => { clearTimeout(timer); reject(tx.error ?? new Error('Device storage could not save this work')); };
    try { if (value) tx.objectStore(STORE_NAME).put(encoded); else tx.objectStore(STORE_NAME).delete(key); } catch (error) { clearTimeout(timer); reject(error); }
  }); } finally { db.close(); }
}
function copyDraft<T>(value:T):T {
  if(value instanceof Blob || value===null || typeof value!=="object") return value;
  if(Array.isArray(value)) return value.map(copyDraft) as T;
  return Object.fromEntries(Object.entries(value).map(([key,item])=>[key,copyDraft(item)])) as T;
}
export function saveAuditDraft<T>(projectId: string, payload: T): Promise<string> {
  const value = { projectId, savedAt: new Date().toISOString(), payload: copyDraft(payload) };
  return serialize(projectId, async () => {
    temporary.set(projectId,value);
    try { await writeRecord(projectId,value); temporary.delete(projectId); } catch { /* Retained only in this tab; the UI must say so. */ }
    return value.savedAt;
  });
}
export function deleteAuditDraft(key: string): Promise<void> {
  return serialize(key, async () => { temporary.set(key,null); try { await writeRecord(key,null); temporary.delete(key); } catch { /* Prevent reopening stale device contents in this session. */ } });
}
export async function downloadDraft(projectId: string, payload: unknown) {
  const data = await encodeDraft({ format: 'site-method-snag-draft', version: 1, projectId, payload },true);
  const url = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: 'application/json' }));
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = `snag-draft-${projectId}.snagdraft`; anchor.click(); setTimeout(() => URL.revokeObjectURL(url),60000);
}
